import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import { FormControl } from "../form-control";
import Input from "../input";
import { Form, Formik, FormikContextType } from "formik";
import { Images } from "@assets/images";
import { Bank } from "@app/types";
import { ChangeEvent, createRef, useEffect, useRef } from "react";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import { createBankValidationSchema } from "@app/validations";

interface BankModalProps {
  isEdit: boolean;

  bank: Bank;
}

function BankModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<BankModalProps>) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const formRef = createRef<
    FormikContextType<{
      bankName: string;
      bankNumber: string;
      ownerName: string;
      filename?: string;
    }>
  >();

  const onSubmit = (values: {
    bankName: string;
    bankNumber: string;
    ownerName: string;
  }) => {
    if (!formRef.current) return;

    if (!inputFileRef.current?.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);

      return;
    }

    const file = inputFileRef.current.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const bank = {
      bankName: values.bankName,
      bankNumber: values.bankNumber,
      ownerName: values.ownerName,
      thumbnail: file,
    };

    portalDialogRef.close({ bank });
  };

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
  };

  useEffect(() => {
    if (portalData?.bank && formRef.current) {
      formRef.current.setValues({
        bankName: portalData.bank.bankName,
        bankNumber: portalData.bank.bankNumber,
        ownerName: portalData.bank.ownerName,
        filename: "",
      });
    }
  }, [portalData?.bank]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[423px] py-7 px-6">
        <div className="text-black font-semibold text-xl">
          {portalData?.isEdit ? "Chỉnh sửa" : "Thêm ngân hàng"}
        </div>
        <Formik
          innerRef={formRef}
          initialValues={{
            bankName: "",
            bankNumber: "",
            ownerName: "",
            filename: "",
          }}
          onSubmit={onSubmit}
          validationSchema={createBankValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <FormControl name="bankName">
              <Input className="mt-7" placeholder="Ngân hàng" />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File ảnh"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <FormControl name="ownerName">
              <Input className="mt-3" placeholder="Chủ tài khoản" />
            </FormControl>
            <FormControl name="bankNumber">
              <Input className="mt-3" placeholder="Số tài khoản" />
            </FormControl>
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label={portalData?.isEdit ? "Lưu" : "Thêm"}
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default BankModal;
